//#region Imports
// Angular
import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormGroup,
} from '@angular/forms';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

// PrimeNg
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { PanelModule } from 'primeng/panel';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';

// Services
import { LoggerService } from '@/Services/log-level.service';

// Components
import { ITypeDocuments } from '../file-upload/file-upload.component';
import { RenderizarHtmlComponent } from '../renderizar-html/renderizar-html.component';

// Interface
import { ITable, TypeColumn, TypeFormat } from '@/interface/general/ITable';
import {
  IAccionBoton,
  IDataPagination,
  IPagination,
} from '@/interface/general/IPagination';
import { IUploadInfo } from '@/interface/general/IUpload';
import { IEmailFormData, IMailData } from './interface/send-mail.interface';
import { SendMailService } from './service/send-mail.service';
import { IParams } from '@/interface/general/params.interface';
import { FileUploadResult } from '../file-upload/interface/file-upload.inteface';
// Enum
import { TabIndex } from './enum/send-mail-enum';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { TableGenericComponent } from '../table-generic/table-generic.component';
import { FileBase64Converter } from '@/shared/service/FileBase64Converter';
import { InboxComponent } from '../inbox/inbox.component';
import { TypeResponse } from '@/common/enums/general/TypeResponse';
import { environment } from 'src/environments/environment';
import { BTN_ACCION } from '@/common/enums/btn_accion';
import { IResponseApi, IResponseApiSS } from '@/interface/general/IResponseApi';
import { Export } from '@/util/exportExcel';
import { ModalGenericComponent } from '../modal-generic/modal-generic.component';
import { DialogService } from 'primeng/dynamicdialog';
import { FooterModalService } from '../footer-modal/footer-modal.service';
import { ToastModule } from 'primeng/toast';
import { TableService } from './service/table.service';
import { LupasComponent } from '../lupas/lupas.component';
import { IFormSearchLupa } from '../lupas/interfaces/lupas.interfaces';
//#endregion

// Tipado
type KeyValueArrayGeneric<T = string | number> = IParams<T>[];

// importaciones tiny/codemirror
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { EditorModule } from '@tinymce/tinymce-angular';
import { html } from '@codemirror/lang-html';
import { EditorView, keymap, highlightActiveLine } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { lineNumbers } from '@codemirror/view';
import { indentOnInput } from '@codemirror/language';
import type { Editor } from 'tinymce';
import { EditorComponent } from '@tinymce/tinymce-angular';
import {
  syntaxHighlighting,
  defaultHighlightStyle,
} from '@codemirror/language';

export interface ICteOrderRequest {
  id_cargue_instancia: number;
  pathwebdescarga: string;
  nombre_archivo: string;
  fecha_inicio_cargue: Date;
  fecha_fin_cargue: Date;
  fecha_registro: Date;
  duracion: string;
  numero_registros_archivo: number;
  numero_registros_procesados: number;
  numero_errores: number;
  observaciones: string;
  acciones?: { botonera?: { id: string; icon: string; tooltip: string }[] }; // Opcional
  botonera?: { id: string; icon: string; tooltip: string }[]; // Opcional
}
// Mapeo de nombres de tabs
// aca es donde se usa el enum TabIndex para mapear los nombres de los tabs
const TAB_NAMES: Record<TabIndex, string> = {
  [TabIndex.LISTA]: 'Lista',
  [TabIndex.BODY]: 'Cuerpo',
  [TabIndex.PREVIEW]: 'Vista Previa',
  [TabIndex.FILES]: 'Archivos',
};

// Mapeo de mensajes de error personalizados
const ERROR_MESSAGES: Record<string, Record<string, string>> = {
  from: {
    required: 'El campo remitente es requerido',
    email: 'Ingrese un email válido para el remitente',
  },
  subject: {
    required: 'El asunto es requerido',
    minlength: 'El asunto debe tener al menos 3 caracteres',
  },
  to: {
    required: 'El destinatario es requerido',
    email: 'Ingrese un email válido para el destinatario',
  },
  cc: {
    invalidEmail: 'Uno o más emails en CC son inválidos',
  },
  body: {
    required: 'El cuerpo del mensaje es requerido',
    minlength: 'El cuerpo debe tener al menos 10 caracteres',
  },
};

enum EACTION_MAIL {
  NEW = 1,
  EDIT = 2,
  DELETE = 3,
}

@Component({
  selector: 'app-send-mail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TabsModule,
    InputTextModule,
    ButtonModule,
    ToolbarModule,
    PanelModule,
    MessageModule,
    RenderizarHtmlComponent,
    // BulkLoandingComponent,
    EditorModule,
    ToastModule,
    ProgressSpinnerModule,
    DialogModule,
    FileUploadComponent,
    TableGenericComponent,
    InboxComponent,
    LupasComponent,
  ],

  templateUrl: './send-mail.component.html',
  styleUrl: './send-mail.component.scss',
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'assets/tinymce/tinymce.min.js' },
    ConfirmationService,
    MessageService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendMailComponent implements OnInit, OnDestroy {
  //#region VARIABLES GLOBALES
  actionToEject = EACTION_MAIL.NEW;
  //#endregion

  //#region VARIABLES TAB BODY
  // Tipado fuerte del formulario usando la interfazs
  // emailForm!: emailForm<IEmailFormFields>; lo pongo asi porque dice emailForm debe ser una propiedad de tu componente, no un import.
  emailForm!: FormGroup;
  contentEditor!: string;
  url!: string; // URL para motivos, se inicializa en ngOnInit
  currentMailId = 0;
  showCcField = false;
  tabDisabled = false;
  isSubmitting = false;
  showDebugInfo = false;
  //#endregion
  visor = '';
  showModal = false;
  editorInstance: Editor | null = null;

  // EXPORT EXCEL
  registerSelectedToExportExcel: any;
  // Campos adicionales como propiedades separadas
  IdfdArchivo = 0;
  CodRadicado = '0';
  //#region VARIABLES TAB
  isLoading = false;
  // Exponer enums al template
  readonly TabIndex = TabIndex;
  activeTabIndex: TabIndex = TabIndex.LISTA;
  // Control de tabs (todos deshabilitados excepto LISTA inicialmente)
  tabsDisabled = {
    [TabIndex.LISTA]: false,
    [TabIndex.BODY]: true,
    [TabIndex.PREVIEW]: true,
    [TabIndex.FILES]: true,
  };
  //#endregion

  //#region PREVIEW MAIL
  renderHtmlMail!: string;
  //#endregion

  // Variables UPLOAD FILE
  showTabFiles = false;
  urlTemplate = ''; // Se inicializará en ngOnInit
  // Opciones para el dropdown de tipo documental
  documentTypeOptions: ITypeDocuments[] = [
    { key: 'Seleccione un tipo', value: 0 },
    { key: 'Documento', value: 1 },
    { key: 'Imagen', value: 2 },
    { key: 'Video', value: 3 },
    { key: 'Audio', value: 4 },
    { key: 'PDF', value: 5 },
    { key: 'Excel', value: 6 },
    { key: 'Word', value: 7 },
    { key: 'Presentación', value: 8 },
  ];
  uploadSubject$ = new Subject<IUploadInfo>();
  filesSubject$ = new BehaviorSubject<{ size: number }[]>([]); // filesSubject$ como BehaviorSubject es bueno para tener siempre un valor actual de archivos
  currentPagination!: IPagination;
  data!: IDataPagination<ICteOrderRequest[]>;
  selectedDocumentType: string | null = null;
  mostrarEstructura = false;
  title = 'Asignación masiva de órdenes';
  tiposDocumentales = ['Excel', 'Otro tipo'];

  uploadConfig!: any; // Se inicializará en ngOnInit

  private fileSelected: any[] = [];

  private subscriptions = new Subscription(); // Para manejar suscripciones

  // VARIABLES DE LA LUPA
  private idFormulario = 'form_mail';
  dataSearhLupaCampo1: IFormSearchLupa = {
    idFormulario: this.idFormulario,
    campo: 'Msy_Mtm_CodMailTemplates',
    label: 'Template Mail',
    required: true,
  };

  // INJECT
  private readonly logger = inject(LoggerService);
  private readonly fb = inject(FormBuilder);
  private service = inject(SendMailService);
  private http = inject(HttpClient);
  public footerService = inject(FooterModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  public tableService = inject(TableService);

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService // private config: DynamicDialogConfig
  ) {
    toObservable(this.footerService.presstButton)
      .pipe(takeUntilDestroyed())
      .subscribe((pressAction) => {
        // Ignoramos el valor inicial 'na' (not applicable).
        if (pressAction.action === 'na') {
          return;
        }
        switch (pressAction.action) {
          case 'saveConfig':
            this.onSubmit();
            break;
          case 'sendMail':
            this.onSendEmail();
            break;
          case 'eliminar':
            break;
          default:
            console.warn('⚠️ Acción no reconocida:', pressAction);
        }
      });

    this.emailForm = this.fb.group({
      Mil_From: this.createControl<string | null>(null, [
        Validators.required,
        Validators.email,
      ]),
      Mil_To: this.createControl<string | null>(null, [
        Validators.required,
        Validators.email,
      ]),
      Mil_Subject: this.createControl<string | null>(null, [
        Validators.required,
      ]),
      Mil_Body: this.createControl<string | null>(null, [Validators.required]),
      Mil_CC: this.createControl<string | null>(null, [Validators.email]),
      Mil_Bcc: this.createControl<string | null>(null, [Validators.email]),
      Msy_Mtm_CodMailTemplates: this.createControl<number | null>(1, [
        Validators.required,
        Validators.min(1),
      ]),
    });
  }

  ngOnInit(): void {
    this.service.loadInitialDataIfNeeded();
    this.setListTable(); // Configura columnas
    this.activeTabIndex = TabIndex.LISTA;

    this.disableAllTabsExceptList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe(); // Limpiar todas las suscripciones
  }

  /**
   *
   * @param value
   * @param validators
   * @returns
   * @augments OJO => No modificar
   */
  private createControl<T>(
    value: T | null,
    validators?: ValidatorFn | ValidatorFn[] | null
  ): FormControl<T | null> {
    return this.fb.control(value, validators);
  }

  //#region LIST TABLE
  // Configuración de paginación
  rowsPerPageOptions = environment.paginationSmall;
  rowsPerPage = this.rowsPerPageOptions[0];
  tableSubject$ = new BehaviorSubject<ITable | null>(null);
  dataSubject$ = new Subject<IDataPagination<IMailData>>();

  setListTable() {
    const table: ITable = {
      fieldId: 'Id',
      paginator: true,
      rowsPerPageOptions: this.rowsPerPageOptions,
      lazyLoadOnInit: true, // Esto es CRUCIAL para que la tabla pida datos al inicio
      columns: [
        {
          field: 'Id',
          label: '',
          type: TypeColumn.NUMBER,
        },
        {
          field: 'Agencia',
          label: 'Agencia',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
          format: TypeFormat.UPPERCASE,
        },
        {
          field: 'Mil_From',
          label: 'From',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
          format: TypeFormat.UPPERCASE,
        },
        {
          field: 'Mil_To',
          label: 'To',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
          format: TypeFormat.UPPERCASE,
        },
        {
          field: 'Mil_Subject',
          label: 'Subject',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
          format: TypeFormat.UPPERCASE,
        },
        {
          field: 'Mil_Met_CodMail_Estado',
          label: 'Estado',
          type: TypeColumn.NUMBER,
          filter: true,
          sort: false,
        },
        {
          field: 'FechaRegistro',
          label: 'F. Registro',
          type: TypeColumn.DATE,
          filter: true,
          sort: true,
          format: TypeFormat.DATETIME,
        },
        {
          field: 'FechaFechaRegistroUpdateRegistro',
          label: 'F. Registro Update',
          type: TypeColumn.DATE,
          filter: true,
          sort: true,
          format: TypeFormat.DATETIME,
        },
        {
          field: 'acciones',
          label: 'Acciones',
          type: TypeColumn.ACTION,
          showActions: false,
          showSelector: false,
          action: (rowData: IMailData, btn: IAccionBoton) =>
            this.handleAction(rowData, btn),
        },
      ],
    };
    this.tableSubject$.next(table); // y elimina this.tableSubject
    //return table;
  }

  onLoadRecordss(event: any, origen: number) {
    // Solo manejar eventos de tabla (origen 2)
    if (origen !== 2) return;

    // Si no hay URL, mostrar error
    if (!this.url) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'URL no configurada para cargar datos',
        life: 3000,
      });
      return;
    }

    // Obtener parámetros de paginación
    const { first, rows } = event;
    const params = new HttpParams()
      .set('page', Math.floor(first / rows) + 1)
      .set('pageSize', rows);

    this.http
      .get<{
        data: IMailData[];
        metadataServerSide?: {
          totalRegitros: number;
          numeroPagina: number;
          filasxPagina: number;
        };
      }>(this.url, { params })
      .subscribe({
        next: (response) => {
          if (!response || !Array.isArray(response.data)) {
            throw new Error('Respuesta inválida del servidor');
          }

          const serverSideData: IMailData[] = response.data.map((item) => ({
            ...item,
            first: event.first,
            rows: event.rows,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            filters: event.filters,
            globalFilter: event.globalFilter,
          }));

          // Usa metadataServerSide directamente de la respuesta, no del primer item
          const metadata = response.metadataServerSide;

          const data: IDataPagination<IMailData> = {
            data: serverSideData,
            pagination: {
              first: metadata
                ? metadata.numeroPagina * metadata.filasxPagina
                : event.first,
              rows: metadata ? metadata.filasxPagina : event.rows,
            },
            totalRecords: metadata
              ? metadata.totalRegitros
              : serverSideData.length,
          };

          this.dataSubject$.next(data);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar datos: ' + err.message,
            life: 3000,
          });
        },
      });
  }

  private disableAllTabsExceptList(): void {
    Object.keys(this.tabsDisabled).forEach((key) => {
      const tabIndex = Number(key) as TabIndex;
      this.tabsDisabled[tabIndex] = tabIndex !== TabIndex.LISTA;
    });
  }

  /**
   * Metodo para exportar registros a excel
   */
  onExportSelectedExcel() {
    Export.Excel(
      this.registerSelectedToExportExcel ?? [],
      'Reporte_Alertas.xlsx'
    );
  }

  handleAction(record: IMailData, btn: IAccionBoton) {
    switch (btn.Exec) {
      case BTN_ACCION.EDITARREGISTRO:
        this.editForm(record);
        break;

      case BTN_ACCION.ELIMINARREGISTRO:
        this.deleteRegister(record);
        break;
      default:
        console.warn('Acción desconocida:', btn.Exec);
    }
  }

  /**
   * Prepara el formulario para un nuevo correo (todo vacío)
   */
  newMail(): void {
    // 1. Resetear el formulario completamente
    this.emailForm.reset();

    // 2. Marcar campos como no tocados/pristinos
    this.emailForm.markAsPristine();
    this.emailForm.markAsUntouched();

    // 3. Resetear ID y referencias
    this.currentMailId = 0;
    // this.IdfdArchivo = '';
    this.CodRadicado = '';
    // this.Modulo = '';

    // 4. Habilitar pestañas
    this.activeTabIndex = TabIndex.BODY;
    this.enableAllTabs();

    // 5. Opcional: cargar valores por defecto desde servicio si es necesario
    this.service.loadInitialDataIfNeeded();
  }

  // Método para crear correo (se llama desde el botón Guardar)
  createMail(): void {
    if (this.emailForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    const mailData: IEmailFormData = {
      ...this.emailForm.getRawValue(),
      Mil_Mtc_CodMail_TipoCorreo: 1,
      Mil_Met_CodMail_Estado: 0,
      Mil_Agn_CodAgencia: '99',
      IdfdArchivo: this.IdfdArchivo,
      CodRadicado: this.CodRadicado,

      CodArchivo: '',
      CodUser: '0',
      FechaRegistro: new Date().toISOString(),
      CodUserUpdate: '0',
      FechaRegistroUpdate: new Date().toISOString(),
      Info: '0|0|0',
      InfoUpdate: '0|0|0',
      Id: this.currentMailId,
    };

    this.service.postCreateMail(mailData).subscribe({
      next: (response: IResponseApiSS) => {
        if (response.status === TypeResponse.OK) {
          // this.handleSuccessResponse(response);
        }
      },
      error: (error: HttpErrorResponse) => {
        // this.handleErrorResponse(error);
      },
    });
  }

  /**
   * Carga los datos de un correo existente para edición
   * @param record Registro seleccionado de la tabla
   */
  editForm(record: IMailData): void {
    this.actionToEject = EACTION_MAIL.EDIT;
    if (!record?.Id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Registro no válido para edición',
        life: 3000,
      });
      return;
    }

    // 1. Resetear formulario antes de cargar nuevos valores
    this.emailForm.reset();

    // 2. Llenar con datos del registro
    setTimeout(() => {
      this.emailForm.patchValue({
        Mil_From: record.Mil_From,
        Mil_To: record.Mil_To,
        Mil_Subject: record.Mil_Subject,
        Mil_Body: record.Mil_Body,
        Mil_CC: record.Mil_CC,
        Mil_Bcc: record.Mil_Bcc,
        Msy_Mtm_CodMailTemplates: record.Msy_Mtm_CodMailTemplates,
      });
      this.cdr.markForCheck(); // Forzar detección de cambios si es necesario
    }, 0);
    // // 3. Guardar IDs importantes
    this.currentMailId = record.Id;
    this.IdfdArchivo = Number(record.IdfdArchivo);
    this.CodRadicado = record.CodRadicado;

    // 4. Marcar como "pristine" para detectar cambios
    this.emailForm.markAsPristine();

    // 5. Habilitar pestañas
    this.activeTabIndex = TabIndex.BODY;
    this.enableAllTabs();
  }

  deleteRegister(record: any) {
    const ref = this.dialogService.open(ModalGenericComponent, {
      header: 'Atención!',
      width: '350px',
      modal: true,
      closable: true,
      maximizable: true,
      dismissableMask: true,
      contentStyle: {
        'border-radius': '8px',
        'box-shadow': '0 10px 30px rgba(51, 27, 27, 0.2)',
      },
      data: {
        title: '¡Atención!',
        message: '¿Desea eliminar este registro definitivamente?',
        tipoConfirmacion: 'error',
        confirmLabel: 'Sí, eliminar',
        cancelLabel: 'Cancelar',
      },
    });
    ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.service.deleteMail(record.Id).subscribe({
          next: (response: IResponseApi) => {
            if (response.status === TypeResponse.OK) {
              this.onLoadRecordss(event, 1); // Recarga los datos
              // Notificación de éxito
              this.messageService.add({
                severity: 'info',
                summary: 'Info Message',
                detail: response.mensaje || 'Error al eliminar el registro',
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: response.mensaje || 'Error al eliminar el registro',
              });
            }
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al eliminar el registro',
            });
          },
        });
      }
    });
  }

  private enableAllTabs() {
    Object.keys(this.tabsDisabled).forEach((key) => {
      this.tabsDisabled[key as unknown as TabIndex] = false;
    });
  }
  //#endregion

  //#region FORM LOGIC

  // Método para obtener los valores del formulario con tipado fuerte
  // getFormValue(): IEmailFormData {
  //   return this.emailForm.getRawValue();
  // }

  /**
   * Método para obtener un control específico con tipado fuerte
   * @param controlName
   * @returns
   */
  getControl<K extends keyof IEmailFormData>(
    controlName: K
  ): FormControl<IEmailFormData[K]> {
    const control = this.emailForm.get(controlName);
    if (!control) {
      throw new Error(`Control ${controlName} not found`);
    }
    return control as unknown as FormControl<IEmailFormData[K]>;
  }

  // Validador personalizado para múltiples emails con mejor tipado
  private multipleEmailValidator(
    control: AbstractControl<string>
  ): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return null; // Campo opcional
    }

    const emails = value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return { invalidEmail: { value: email } };
      }
    }

    return null;
  }

  // Mejorado con tipos más específicos
  // isFieldInvalid(fieldName: keyof IEmailFormData): boolean {
  //   const field = this.getControl(fieldName);
  //   return field.invalid && (field.dirty || field.touched);
  // }

  isFieldInvalid(field: keyof IEmailFormData): boolean {
    const control = this.emailForm.get(field);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }
  // Mejorado con mensajes personalizados y tipado fuerte
  getFieldError(fieldName: keyof IEmailFormData): string {
    const control = this.getControl(fieldName);
    const errors = control.errors;

    if (!errors) {
      return '';
    }

    const fieldErrors = ERROR_MESSAGES[fieldName];

    // Verificar errores en orden de prioridad
    if (errors['required'] && fieldErrors?.['required']) {
      return fieldErrors['required'];
    }

    if (errors['email'] && fieldErrors?.['email']) {
      return fieldErrors['email'];
    }

    if (errors['minlength'] && fieldErrors?.['minlength']) {
      const requiredLength = errors['minlength'].requiredLength;
      return `${fieldErrors['minlength']} (${requiredLength} caracteres)`;
    }

    if (errors['invalidEmail'] && fieldErrors?.['invalidEmail']) {
      return `${fieldErrors['invalidEmail']}: ${errors['invalidEmail'].value}`;
    }

    // Fallback genérico
    return `Campo ${fieldName} inválido`;
  }

  toggleCcField(): void {
    this.showCcField = !this.showCcField;
    if (!this.showCcField) {
      this.emailForm.get('Mil_CC')?.setValue(null);
    }
  }

  resetForm(): void {
    this.emailForm.reset();
    this.emailForm.markAsPristine();
    this.emailForm.markAsUntouched();
    this.showCcField = false;

    // this.activeTabIndex = TabIndex.BODY;
  }

  /**
   * Método para enviar el formulario de correo electrónico
   * Utiliza el método tipado getFormValue() para obtener los datos del formulario
   */
  onSubmit(): void {
    if (this.emailForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    const formData = this.prepareFormData();

    switch (this.actionToEject) {
      case EACTION_MAIL.NEW:
        this.service.postCreateMail(formData).subscribe({
          next: (response: IResponseApiSS) => {
            if (response.status === TypeResponse.OK) {
              this.handleSuccessResponse(response, formData.Id > 0);

              // Habilita y muestra el preview
              this.tabsDisabled[TabIndex.PREVIEW] = false;
              this.activeTabIndex = TabIndex.PREVIEW;
              this.tabsDisabled[TabIndex.FILES] = false;
              this.activeTabIndex = TabIndex.FILES;

              if (!formData.Id && response.data?.id) {
                this.currentMailId = response.data.id;
                this.tableService.currentMailId = response.data.id;
                this.actionToEject = EACTION_MAIL.EDIT;
              }
            }
          },
          error: (err: HttpErrorResponse) => {
            this.handleErrorResponse(err);
          },
        });
        break;
      case EACTION_MAIL.EDIT:
        this.service.PutUpdateMail(formData).subscribe({
          next: (response: IResponseApiSS) => {
            if (response.status === TypeResponse.OK) {
              this.handleSuccessResponse(response, formData.Id > 0);

              // Habilita y muestra el preview
              this.tabsDisabled[TabIndex.PREVIEW] = false;
              this.activeTabIndex = TabIndex.PREVIEW;
              this.tabsDisabled[TabIndex.FILES] = false;
              this.activeTabIndex = TabIndex.FILES;

              if (!formData.Id && response.data?.id) {
                this.currentMailId = response.data.id;
                this.actionToEject = EACTION_MAIL.EDIT;
              }
            }
          },
          error: (err: HttpErrorResponse) => {
            this.handleErrorResponse(err);
          },
        });
        break;
      default:
        // Prevent using observable before assignment
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Acción de correo no válida.',
          life: 3000,
        });
        return;
    }
  }

  private showSuccessAndGoToPreview(
    response: IResponseApiSS,
    isUpdate: boolean
  ): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: isUpdate ? 'Correo actualizado' : 'Correo creado',
      life: 3000,
    });

    // Actualizar ID si es nuevo
    if (!isUpdate && response.data?.id) {
      this.currentMailId = response.data.id;
    }

    // Habilitar pestañas y mostrar preview
    this.enableAllTabs();
    this.activeTabIndex = TabIndex.PREVIEW;
  }

  private prepareFormData(): IEmailFormData {
    return {
      ...this.emailForm.getRawValue(),
      Mil_Mtc_CodMail_TipoCorreo: 1,
      Mil_Met_CodMail_Estado: 0,
      Mil_Agn_CodAgencia: '99',
      IdfdArchivo: this.IdfdArchivo,
      CodRadicado: this.CodRadicado,
      // Msy_Mtm_CodMailTemplates: 1,
      CodArchivo: '',
      CodUser: '0',
      FechaRegistro: new Date().toISOString(),
      CodUserUpdate: '0',
      FechaRegistroUpdate: new Date().toISOString(),
      Info: '0|0|0',
      InfoUpdate: '0|0|0',
      Id: this.currentMailId,
    };
  }

  private handleSuccessResponse(
    response: IResponseApiSS,
    isUpdate: boolean
  ): void {
    if (response.status === TypeResponse.OK) {
      // Actualiza el ID si es un nuevo correo
      if (!isUpdate && response.data?.id) {
        this.currentMailId = response.data.id;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: isUpdate ? 'Correo actualizado' : 'Correo creado',
        life: 3000,
      });
    }
  }

  private handleErrorResponse(error: HttpErrorResponse): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al guardar: ' + error.message,
      life: 3000,
    });
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.emailForm.controls).forEach((key) => {
      const control = this.emailForm.get(key);
      control?.markAsTouched();
    });
  }

  private getFormErrors(): Record<string, ValidationErrors | null> {
    const errors: Record<string, ValidationErrors | null> = {};
    Object.keys(this.emailForm.controls).forEach((key) => {
      const control = this.emailForm.get(key);
      if (control?.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }
  //#endregion

  //#region TAB LOGIC
  onTabChange(index: number): void {
    const previousTab = this.activeTabIndex;
    const newTabIndex = index as TabIndex;

    switch (newTabIndex) {
      case TabIndex.PREVIEW:
        this.handlePreviewTabEnter();
        break;
      case TabIndex.FILES:
        this.handleFilesTabEnter();
        this.showTabFiles = true; // Mostrar tab de archivos
        break;
      default:
        this.logger.warn(`Índice de tab desconocido: ${newTabIndex}`);
    }

    if (!this.isValidTabIndex(newTabIndex)) {
      this.logger.warn(`Índice de tab inválido: ${newTabIndex}`);
      return;
    }

    this.activeTabIndex = newTabIndex;

    this.logger.log(
      `Tab cambiado de ${this.getTabName(
        previousTab
      )} a ${this.getActiveTabName()}`
    );

    this.handleTabChange(newTabIndex, previousTab);
  }

  // Manejo de cambio de tabs

  private isValidTabIndex(index: number): index is TabIndex {
    return Object.values(TabIndex).includes(index as TabIndex);
  }

  // Lógica específica para cada tab con mejor estructura
  private handleTabChange(
    newTabIndex: TabIndex,
    previousTabIndex: TabIndex
  ): void {
    this.logger.log(`Cambiando de tab ${previousTabIndex} a ${newTabIndex}`);

    switch (newTabIndex) {
      case TabIndex.BODY:
        this.handleBodyTabEnter();
        break;

      case TabIndex.PREVIEW:
        this.handlePreviewTabEnter();
        break;

      case TabIndex.FILES:
        this.handleFilesTabEnter();
        break;

      default:
        this.logger.warn(`Tab desconocido: ${newTabIndex}`);
    }
  }

  private handleBodyTabEnter(): void {
    this.logger.log('Entrando al tab Cuerpo');
    // Lógica específica para el tab de cuerpo
  }

  private handlePreviewTabEnter(): void {
    this.logger.log('Entrando al tab Vista Previa');
    if (!this.emailForm.valid) {
      this.logger.warn(
        'Formulario inválido para vista previa',
        this.getFormErrors()
      );
    } else {
      this.getPreviewMail();
    }
  }

  /**
   * Consultamos el html renderizado
   */
  private getPreviewMail() {
    const params: KeyValueArrayGeneric<number> = [
      { key: 'idMail', value: 23 },
      { key: 'IdTemplate', value: 1 },
    ];

    this.service.getWitchParams(params, 'mail/GetHtmlByIdMail').subscribe({
      next: (resp: any) => {
        this.renderHtmlMail = resp.data.HtmlTemplate;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.cdr.markForCheck();
      },
    });
  }

  private handleFilesTabEnter(): void {
    this.logger.log('Entrando al tab Archivos');
    this.showTabFiles = true;
    this.tableService.setTable();
    // Lógica específica para el tab de archivos
  }

  // Obtener nombre del tab activo
  getActiveTabName(): string {
    return this.getTabName(this.activeTabIndex);
  }

  // Obtener nombre del tab por índice con mejor tipado
  getTabName(index: TabIndex): string {
    return TAB_NAMES[index] || 'Desconocido';
  }

  // Cambiar programáticamente a un tab específico
  switchToTab(tabIndex: TabIndex): void {
    if (this.isValidTabIndex(tabIndex)) {
      this.activeTabIndex = tabIndex;
      this.logger.log(
        `Cambiando programáticamente al tab: ${this.getTabName(tabIndex)}`
      );
    } else {
      this.logger.error(`Índice de tab inválido: ${tabIndex}`);
    }
  }

  // Métodos de utilidad para el template
  get isFormValid(): boolean {
    return this.emailForm.valid;
  }

  get isFormDirty(): boolean {
    return this.emailForm.dirty;
  }

  get canSubmit(): boolean {
    return this.isFormValid && !this.isSubmitting;
  }

  //#endregion

  //#region UPLOAD FILE

  /**
   * Obtiene los idetalles de una instancia específica.
   * Este método es llamado cuando se selecciona una instancia en la tabla.
   * @param id
   */
  // getInstanceDetail(id: number): void {
  //   this.logger.debug('[getInstanceDetail]: Detalle de instancia:', id);
  //   alert(`Detalle de instancia: ${id}`);
  // }


  onSelectedFiles(event: FileUploadResult[]): void {
    // this.filesSubject$.next(archivos ? [event.file] : []);
    this.logger.debug('[onSelectedFiles]: Archivos seleccionado', event);
  }

  async onUpload(event: FileUploadResult[]): Promise<void> {
    this.logger.debug('[onUpload]: Evento onUpload recibido', event);

    if (!event || event.length === 0) {
      alert('No se seleccionaron archivos');
      return;
    }

    try {
      // Convertir todos los archivos a base64
      const filesWithBase64 = await Promise.all(
        event.map(async (fileResult) => {
          const base64 = await FileBase64Converter.convertFileToBase64(
            fileResult.file
          );
          return {
            Base64Content: base64,
            FileExtension: '.' + fileResult.file.name.split('.').pop(),
          };
        })
      );

      const dataToSend = {
        FilesBase64: filesWithBase64,
        Id: this.currentMailId,
        CodGlobal: '99', // Esto debería venir de algún lugar de tu aplicación
        CodTiposDocumentales: 2, // Esto debería venir de algún lugar de tu aplicación
      };

      this.logger.debug(
        '[onUpload]: Datos a enviar para carga de archivos',
        dataToSend
      );

      this.service.uploadFileBase64(dataToSend).subscribe({
        next: (response) => {
          this.logger.log('Archivos cargados exitosamente:' + response);
          this.tableService.onLoadRecords();
          alert(`Archivo(s) cargado(s) exitosamente`);
        },
        error: (error) => {
          this.logger.error('Error al cargar archivos:', error);
          alert('Error al cargar los archivos. Intente de nuevo.');
        },
      });
    } catch (error) {
      this.logger.error('Error al procesar archivos:', error);
      alert(
        error instanceof Error ? error.message : 'Error al procesar archivos'
      );
    }
  }

  //#endregion

  uploadError($event: string) {
    throw new Error('Method not implemented.');
  }

  //#region
  confirm1(message: string) {
    this.confirmationService.confirm({
      // target: event.target as EventTarget,
      message: 'Are you sure that you want to proceed?',
      header: 'Confirmation',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Save',
      },
      accept: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'You have accepted',
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Rejected',
          detail: 'You have rejected',
          life: 3000,
        });
      },
    });
  }
  //#endregion

  onSendEmail(): void {
    const emailData = {
      ...this.emailForm.getRawValue(),
      id: this.currentMailId,
      // attachments: this.getAttachments(), // Método que obtiene los adjuntos
    };

    this.confirmationService.confirm({
      message: '¿Está seguro que desea enviar este correo?',
      header: 'Confirmación',
      icon: 'pi pi-send',
      accept: () => {
        this.service.postSendMail(emailData).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Correo enviado correctamente',
              life: 3000,
            });
          },
          error: (err) => {
            this.handleErrorResponse(err);
          },
        });
      },
    });
  }

  onUpdateFromPreview(): void {
    this.confirmationService.confirm({
      message: '¿Desea actualizar el contenido del correo?',
      header: 'Confirmación',
      icon: 'pi pi-refresh',
      accept: () => {
        this.activeTabIndex = TabIndex.BODY; // Volver al formulario
        // Aquí podrías cargar los datos actualizados si es necesario
      },
    });
  }

  // LÓGICA DE CODEMIRROR / TINYMCE
  private cmView: EditorView | null = null;
  init: Record<string, any> = {
    plugins: 'lists link image table help wordcount image',
    toolbar:
      'undo redo | bold italic | alignleft aligncenter alignright | image | customCode',
    base_url: '/assets/tinymce',
    suffix: '.min',
    file_picker_types: 'image',
    font_formats:
      'Arial=arial; Courier New=courier new; Georgia=georgia; Times New Roman=times new roman; Verdana=verdana',

    file_picker_callback: (
      callback: (url: string, meta?: any) => void,
      _value: string,
      meta: any
    ) => {
      if (meta.filetype === 'image') {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = () => {
            const id = 'blobid' + new Date().getTime();
            const activeEditor = (window as any).tinymce?.activeEditor;
            const blobCache = activeEditor?.editorUpload?.blobCache;

            if (!blobCache) return;

            const base64 = reader.result as string;
            const blobInfo = blobCache.create(id, file, base64);
            blobCache.add(blobInfo);

            callback(blobInfo.blobUri(), { title: file.name });
          };
          reader.readAsDataURL(file);
        };

        input.click();
      }
    },

    setup: (editor: Editor) => {
      this.editorInstance = editor;

      editor.ui.registry.addButton('customCode', {
        icon: 'code-sample',
        tooltip: 'Ver/editar HTML con CodeMirror',
        onAction: () => {
          this.openModal();
        },
      });

      editor.on('init', () => {
        const content = this.visor || '';
        editor.setContent(content);
      });

      editor.on('change', () => {
        const content = editor.getContent();
        this.visor = content;
        this.emailForm.get('Mil_Body')?.setValue(content);
      });
    },
  };

  openModal(): void {
    this.showModal = true;
    this.visor = this.editorInstance?.getContent() || '';
    this.cdr.detectChanges();
    setTimeout(() => this.initCodeMirror(), 150);
  }

  initCodeMirror(): void {
    const container = document.getElementById('codemirror-editor');
    if (!container) return;

    // Limpiar instancias anteriores
    if (this.cmView) {
      this.cmView.destroy();
      this.cmView = null;
      container.innerHTML = '';
    }

    const state = EditorState.create({
      doc: this.visor,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        history(),
        indentOnInput(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        syntaxHighlighting(defaultHighlightStyle),
        html(),
        oneDark,
      ],
    });

    this.cmView = new EditorView({
      state,
      parent: container,
    });

    const btn = document.getElementById('apply-btn');
    if (btn) {
      btn.onclick = () => {
        const updatedHtml = this.cmView!.state.doc.toString();

        // ✅ Sincronización bidireccional
        this.visor = updatedHtml;
        this.editorInstance?.setContent(updatedHtml);

        // ✅ Cerrar modal
        this.showModal = false;
        this.cdr.detectChanges();
      };
    }
  }

  onTinyMCEInit(event: any): void {
    const editor: Editor = event.editor;

    setTimeout(() => {
      editor.setContent?.(this.visor || '');
    }, 500);
  }
}
